/**
 * Turn a solved item list into the build payload the UI renders — locally.
 *
 * A browser solve produces 16 item ids and 6 invested points and nothing else:
 * the WASM kernel scores packed stat rows and has no names, icons or set
 * bonuses. Until the catalog existed the client genuinely could not fill that in
 * — it could see that items shared a set, but not what the set awarded — so
 * every local solve ended in a `gen/hydrate` round trip. The catalog carries
 * both halves now, so that request can go.
 *
 * This is a port of `backend/gen/engine/build_serializer.serialize_build`, and
 * the port is the risk: a local build and a server build must reach the UI
 * identically or they drift apart silently, which is exactly what that module's
 * docstring warns about. `hydrateParity.test.js` pins it against a fixture
 * generated from the Python, in the manner of the solver oracles.
 */

import { deriveSetBonuses } from '../setBonusesFromCatalog';

const SLOT_COUNT = 16;

/**
 * Build the payload the UI renders from ids alone.
 *
 * @param {object} catalog  loaded item catalog (see services/itemCatalog)
 * @param {number[]} itemIds  16 slots; 0 or unknown means empty
 * @param {number[]} investedPoints  the six investable characteristics
 * @returns {{items: Array, invested_points: number[], set_bonuses: object}}
 */
export function hydrateBuildLocally(catalog, itemIds, investedPoints) {
    const ids = Array.from({ length: SLOT_COUNT }, (_, i) => Number(itemIds?.[i]) || 0);
    const items = ids.map((id) => (id ? catalog.itemById.get(id) || null : null));

    return {
        items,
        invested_points: Array.from(investedPoints || []),
        set_bonuses: deriveSetBonuses(catalog, items),
    };
}
